import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "quill/dist/quill.snow.css";

import {
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  Box,
  IconButton,
  Divider,
  Card,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";

import EastIcon from "@mui/icons-material/East";
import ImageIcon from "@mui/icons-material/Image";
import { addExhibitorProduct } from "../../../Reducer/exhibitorProductSlice";

const AddProduct = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const inputRefBg = useRef(null);
  const inputRefBanner = useRef(null);

  const [productData, setProductData] = useState({
    event_name: "",
    location: "",
    total_exhibitor: "",
    price: "",
    industry: "",
    slug: "",
    description: "",
    events_date: "",
    events_date_end: "",
    events_date_text: "",
    above_banner_statement: "",
    below_banner_statement: "",
    background_img: null,
    banner_img: null,
    product_type: "event",
    credits: 0,
  });

  const [backgroundImg, setBackgroundImg] = useState(null);
  const [bannerImg, setBannerImg] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProductData({ ...productData, [name]: value });
  };

  const handleEditorChange = (value) => {
    setProductData({ ...productData, description: value });
  };

  const handleImageUpload = (e) => {
    const { name, files } = e.target;
    const file = files[0];
    if (name === "background_img") {
      setBackgroundImg(URL.createObjectURL(file));
      setProductData({ ...productData, background_img: file });
    } else if (name === "banner_img") {
      setBannerImg(URL.createObjectURL(file));
      setProductData({ ...productData, banner_img: file });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const requiredFields = productData.product_type === "credit"
      ? ["event_name", "price", "slug", "product_type", "credits"]
      : Object.keys(productData);

    for (let field of requiredFields) {
      if (productData[field] === "" || productData[field] === null || productData[field] === undefined) {
        toast.error(`Please fill required field: ${field.replace("_", " ")}`);
        return;
      }
    }
    const formData = new FormData();
    Object.entries(productData).forEach(([key, value]) => {
      formData.append(key, value);
    });
    dispatch(addExhibitorProduct(formData))
      .unwrap()
      .then(() => {
        toast.success("Product Created Successfully");
        navigate("/dashboard/exhibitor-productlist");
      })
      .catch((error) => {
        toast.error(error.message || "Failed to add product");
      });
  };

  const renderField = (label, name, type = "text") => (
    <Box>
      <Typography fontWeight={700} mb={0.7} color="#111" letterSpacing={0.5}>
        {label}
      </Typography>
      <TextField
        fullWidth
        name={name}
        type={type}
        value={productData[name]}
        onChange={handleChange}
        required
        size="medium"
        variant="outlined"
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            "&.Mui-focused fieldset": {
              borderColor: "#1976d2",
              boxShadow: "0 0 8px #1976d2aa",
            },
          },
        }}
      />
    </Box>
  );

  const uploadBox = (label, name, imgSrc, inputRef) => (
    <Box
      onClick={() => inputRef.current.click()}
      sx={{
        border: "2.5px dashed #1976d2",
        borderRadius: 3,
        height: 150,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        cursor: "pointer",
        textAlign: "center",
        backgroundColor: "#f0f7ff",
        color: "#1976d2",
        transition: "background-color 0.3s ease, border-color 0.3s ease",
        "&:hover": {
          backgroundColor: "#d0e4ff",
          borderColor: "#0d47a1",
          boxShadow: "0 4px 12px rgba(25, 118, 210, 0.4)",
        },
      }}
    >
      <ImageIcon sx={{ fontSize: 48 }} />
      <Typography variant="body1" mt={1} fontWeight={600}>
        Click to upload {label}
      </Typography>
      <input
        type="file"
        name={name}
        onChange={handleImageUpload}
        ref={inputRef}
        hidden
        accept="image/*"
      />
      {imgSrc && (
        <img
          src={imgSrc}
          alt={name}
          style={{ marginTop: 10, maxHeight: 90, borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
        />
      )}
    </Box>
  );

  return (
    <Paper
      elevation={6}
      sx={{
        padding: 5,
        margin: "auto",
        mt: 6,
        mb: { xs: "72%", sm: 0 },
        maxWidth: 1200,
        borderRadius: 4,
        background:
          "linear-gradient(145deg, #ffffff 0%, #f0f4ff 100%)",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          pb: 1,
          borderBottom: "2px solid #1976d2",
        }}
      >
        <Typography
          variant="h4"
          fontWeight={800}
          letterSpacing={1}
          color="#0d47a1"
          sx={{ textShadow: "1px 1px 2px #bbdefb" }}
        >
          Add New Exhibitor Product
        </Typography>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            border: "2px solid #1976d2",
            borderRadius: 3,
            display: "flex",
            alignItems: "center",
            px: 2.5,
            py: 1,
            color: "#1976d2",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "#1565c0",
              color: "#1976d2",
              boxShadow: "0 0 8px #1565c0",
              transition: "all 0.25s ease",
            },
          }}
          aria-label="go back"
        >
          <EastIcon fontSize="small" />
          <Typography variant="body2" ml={1} fontWeight={700}>
            Back
          </Typography>
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 4,
                p: 4,
                boxShadow:
                  "0 6px 18px rgba(25, 118, 210, 0.1)",
              }}
            >
              <Typography
                variant="h5"
                gutterBottom
                color="#0d47a1"
                fontWeight={900}
                letterSpacing={0.8}
                mb={3}
              >
                🎫 Event Info
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  {renderField(productData.product_type === "credit" ? "Product Name *" : "Event Name *", "event_name")}
                </Grid>
                {productData.product_type !== "credit" && (
                  <>
                    <Grid item xs={12} sm={6}>
                      {renderField("Location *", "location")}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {renderField("Total Exhibitors *", "total_exhibitor")}
                    </Grid>
                  </>
                )}
                <Grid item xs={12} sm={6}>
                  {renderField("Price ($) *", "price")}
                </Grid>
                {productData.product_type !== "credit" && (
                  <Grid item xs={12} sm={6}>
                    {renderField("Industry *", "industry")}
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  {renderField("Slug (Unique Identifier) *", "slug")}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <Typography fontWeight={700} mb={0.7} color="#111" letterSpacing={0.5}>
                      Product Type *
                    </Typography>
                    <Select
                      name="product_type"
                      value={productData.product_type}
                      onChange={handleChange}
                      required
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="event">🎫 Normal Event</MenuItem>
                      <MenuItem value="credit">💳 Credit Balance</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {productData.product_type === "credit" && (
                  <Grid item xs={12} sm={6}>
                    {renderField("Credits (Amount to add to wallet) *", "credits", "number")}
                  </Grid>
                )}
              </Grid>
            </Card>
          </Grid>

          {productData.product_type !== "credit" && (
            <>
              <Grid item xs={12}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 4,
                    p: 4,
                    boxShadow:
                      "0 6px 18px rgba(25, 118, 210, 0.1)",
                  }}
                >
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    gutterBottom
                    color="#0d47a1"
                    letterSpacing={0.8}
                    mb={3}
                  >
                    📝 Product Description
                  </Typography>
                  <Box
                    sx={{
                      border: "1.5px solid #1976d2",
                      borderRadius: 3,
                      minHeight: 270,
                      "& .ql-container": {
                        minHeight: 220,
                        borderBottomLeftRadius: 12,
                        borderBottomRightRadius: 12,
                      },
                      "& .ql-toolbar": {
                        borderTopLeftRadius: 12,
                        borderTopRightRadius: 12,
                      },
                    }}
                  >
                    <ReactQuill
                      value={productData.description}
                      onChange={handleEditorChange}
                      required
                      placeholder="Write detailed product or event description here..."
                    />
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 4,
                    p: 4,
                    boxShadow:
                      "0 6px 18px rgba(25, 118, 210, 0.1)",
                  }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    color="#0d47a1"
                    letterSpacing={0.8}
                    fontWeight={900}
                    mb={3}
                  >
                    📅 Event Dates
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      {renderField("Start Date *", "events_date", "date")}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {renderField("End Date *", "events_date_end", "date")}
                    </Grid>
                    <Grid item xs={12}>
                      {renderField("Event Date Description *", "events_date_text")}
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 4,
                    p: 4,
                    boxShadow:
                      "0 6px 18px rgba(25, 118, 210, 0.1)",
                  }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    color="#0d47a1"
                    letterSpacing={0.8}
                    fontWeight={900}
                    mb={3}
                  >
                    🖼 Banner Statements
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      {renderField("Above Banner Statement *", "above_banner_statement")}
                    </Grid>
                    <Grid item xs={12}>
                      {renderField("Below Banner Statement *", "below_banner_statement")}
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 4,
                    p: 4,
                    boxShadow:
                      "0 6px 18px rgba(25, 118, 210, 0.1)",
                  }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    color="#0d47a1"
                    letterSpacing={0.8}
                    fontWeight={900}
                    mb={3}
                  >
                    📤 Upload Images
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      {uploadBox("background image", "background_img", backgroundImg, inputRefBg)}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {uploadBox("banner image", "banner_img", bannerImg, inputRefBanner)}
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </>
          )}

          <Grid item xs={12} sx={{ mt: 4, textAlign: "center" }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              sx={{
                px: 6,
                py: 1.8,
                fontSize: "1.1rem",
                borderRadius: 4,
                background:
                  "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
                boxShadow: "0 5px 15px rgba(25, 118, 210, 0.4)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background:
                    "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
                  boxShadow: "0 8px 24px rgba(21, 101, 192, 0.6)",
                },
              }}
            >
              🚀 Add Product
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default AddProduct;
